import React from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { Seal } from '../components/brand/Seal';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Eyebrow } from '../components/ui/Eyebrow';
import { commitments, clinicians } from '../data/site';
import { useBrand } from '../contexts/BrandContext';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
export function About() {
  const {
    brand
  } = useBrand();
  useDocumentMeta({
    title: `Who we are — ${brand.name}`,
    description: 'Vesta exists because perimenopause care is the largest untreated gap in medicine, and because most clinicians were never taught it.'
  });
  return <main className="w-full bg-cream" data-id="element-385" data-mp="2x7my8c3gmoqk">
      <PageHeader eyebrow="Who we are" title="Named for the fire nobody was allowed to let go out." intro="Vesta kept the hearth. Her fire was tended continuously, by women, because a household without it did not survive the winter. It seemed like the right name for a practice built around the thing everybody stopped tending." data-id="element-386" data-mp="1guy75fcx2x1a" />

      <section className="bg-cream" data-id="element-387" data-mp="1pkjkgw0cmd7g">
        <div className="mx-auto grid max-w-shell gap-14 px-6 pb-20 pt-10 md:px-8 md:pb-24 md:pt-14 lg:grid-cols-[1.15fr_0.85fr] lg:gap-20" data-id="element-388" data-mp="23i864628podk">
          <div data-id="element-389" data-mp="1yiltnyu8kc5t">
            <h2 className="max-w-2xl font-serif text-4xl leading-[1.06] text-ink md:text-[3rem]" data-id="element-390" data-mp="1yi29scl0zdp5">
              In 2002 a study was misread, and twenty years of women paid for it.
            </h2>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink-soft" data-id="element-391" data-mp="28h1i1u1vp7bm">
              The Women’s Health Initiative was reported as proof that hormone therapy causes breast
              cancer. Prescribing collapsed by around 80% within a few years. An entire generation of
              clinicians trained in the aftermath, learning to change the subject.
            </p>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft" data-id="element-392" data-mp="2r9dkegsj88lr">
              The study has since been substantially reinterpreted. The correction never got the
              headlines. What got left behind was an information vacuum, and millions of women
              walking into appointments to be told they were stressed, or too young, or that their
              bloods were normal.
            </p>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-ink-soft" data-id="element-393" data-mp="22ncg0bd1hdp1">
              That is the gap we exist in. Not because the answers are unknown — the evidence is
              there and has been for years. Because almost nobody was trained to give them, and
              almost nobody thought this was a market worth building for.
            </p>
            <p className="mt-8 max-w-2xl font-serif text-2xl italic leading-snug text-brick" data-id="element-394" data-mp="2ljuedbme8bu9">
              Someone should have told you years ago. We are not going to pretend that is fixed by a
              subscription — but we can at least stop being the ones withholding it.
            </p>
          </div>
          <div className="flex items-start justify-center lg:justify-end" data-id="element-395" data-mp="jo52mso7pj5m">
            <Seal className="h-56 w-56 text-brick/70" data-id="element-396" data-mp="ew4ysp50u63r" />
          </div>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-397" data-mp="9ws9lxqogmy0">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-398" data-mp="9xtka13s77f7">
          <SectionHeading eyebrow="Our clinicians" title="Trained in this specifically." intro="Menopause Society certification means sitting a dedicated examination in this field. Most medical degrees devote a matter of hours to it across the entire programme." data-id="element-399" data-mp="4zs2rkm07g5u" />
          <div className="mt-14 grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:gap-16" data-id="element-400" data-mp="smgnsf6b7oqo">
            <img src={brand.images.clinician} alt="A clinician in her fifties looking directly at the camera." className="h-80 w-full rounded-brand object-cover lg:h-full" loading="lazy" data-id="element-401" data-mp="oleqi3vydmrc" />
            <ul data-id="element-402" data-mp="113z8jaxyk4gk">
              {clinicians.map(clinician => <li key={clinician.name} className="rule py-6 first:border-t-0 first:pt-0" data-id="element-403" data-mp="112o5xeg912r1">
                  <h3 className="font-serif text-2xl leading-snug text-ink" data-id="element-404" data-mp="15waaejc9e0br">{clinician.name}</h3>
                  <p className="mt-1.5 text-brick" data-id="element-405" data-mp="r7e2qne46pbo">{clinician.role}</p>
                  <p className="mt-3 max-w-2xl leading-relaxed text-ink-soft" data-id="element-406" data-mp="w44dqwb2auq8">{clinician.detail}</p>
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      <section className="bg-cream-deep" data-id="element-407" data-mp="1ottldsia1t0t">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-408" data-mp="1oss4v8ihsebp">
          <Eyebrow className="text-brick" data-id="element-409" data-mp="1at0ncxsrofsy">What we won’t do</Eyebrow>
          <h2 className="mt-5 max-w-2xl font-serif text-4xl leading-[1.06] text-ink md:text-[3rem]" data-id="element-410" data-mp="1euuiyro3utm9">
            Four commitments, each of which costs us money.
          </h2>
          <ul className="mt-12 grid gap-px overflow-hidden rounded-brand border border-ink/15 bg-ink/15 md:grid-cols-2" data-id="element-411" data-mp="3g8mi749hv0u">
            {commitments.map(item => <li key={item.title} className="bg-cream p-8" data-id="element-412" data-mp="3s0is1yevseyk">
                <h3 className="font-serif text-2xl leading-snug text-ink" data-id="element-413" data-mp="3n3scrdawhne7">{item.title}</h3>
                <p className="mt-4 leading-relaxed text-ink-soft" data-id="element-414" data-mp="5vf66mecffnx">{item.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-415" data-mp="tpseyzqknw5" />
    </main>;
}